import { lazy, Suspense, useEffect } from "react";
import { useMousePosition } from "@/hooks/useMousePosition";
import { useLenis } from "@/hooks/useLenis";
import { useScroll } from "framer-motion";
import { Navbar } from "./Navbar";
import { HeroCore } from "./HeroCore";
import { SectionDivider } from "@/components/common/SectionDivider";
import { ScrollProgressBar } from "@/components/common/ScrollProgressBar";
import { CustomCursor } from "@/components/common/CustomCursor";
import { GlobalNoiseOverlay } from "@/components/common/GlobalNoiseOverlay";
import { ScrollYContext } from "@/components/common/ScrollContext";
import { DeferredMount } from "@/components/common/DeferredMount";

// Raw import()s kept separately from lazy() so they can also be triggered
// eagerly in the background (see preload effect below) -- once a dynamic
// import has resolved once, React.lazy's later use of the same import()
// call resolves instantly from the module cache instead of hitting the
// network, which is what makes scrollToSection's force-mount reliable.
const importers = {
  LaptopShowcase: () => import("./LaptopShowcase"),
  AnimatedStats: () => import("./AnimatedStats"),
  FeatureCards: () => import("./FeatureCards"),
  ClosingCtaBand: () => import("./ClosingCtaBand"),
  DemoShowcase: () => import("@/components/demos/DemoShowcase"),
  ResultsSection: () => import("@/components/results/ResultsSection"),
  ConciergeSection: () => import("@/components/concierge/ConciergeSection"),
  CaseStudiesSection: () => import("@/components/casestudies/CaseStudiesSection"),
  PricingSection: () => import("@/components/pricing/PricingSection"),
  TrustSection: () => import("@/components/trust/TrustSection"),
  StickyTrustBar: () => import("@/components/trust/StickyTrustBar"),
  WhatsAppBubble: () => import("./WhatsAppBubble"),
  BookingSection: () => import("@/components/booking/BookingSection"),
};

const LaptopShowcase = lazy(() => importers.LaptopShowcase().then((m) => ({ default: m.LaptopShowcase })));
const AnimatedStats = lazy(() => importers.AnimatedStats().then((m) => ({ default: m.AnimatedStats })));
const FeatureCards = lazy(() => importers.FeatureCards().then((m) => ({ default: m.FeatureCards })));
const ClosingCtaBand = lazy(() => importers.ClosingCtaBand().then((m) => ({ default: m.ClosingCtaBand })));
const DemoShowcase = lazy(() => importers.DemoShowcase().then((m) => ({ default: m.DemoShowcase })));
const ResultsSection = lazy(() => importers.ResultsSection().then((m) => ({ default: m.ResultsSection })));
const ConciergeSection = lazy(() => importers.ConciergeSection().then((m) => ({ default: m.ConciergeSection })));
const CaseStudiesSection = lazy(() => importers.CaseStudiesSection().then((m) => ({ default: m.CaseStudiesSection })));
const PricingSection = lazy(() => importers.PricingSection().then((m) => ({ default: m.PricingSection })));
const TrustSection = lazy(() => importers.TrustSection().then((m) => ({ default: m.TrustSection })));
const StickyTrustBar = lazy(() => importers.StickyTrustBar().then((m) => ({ default: m.StickyTrustBar })));
const WhatsAppBubble = lazy(() => importers.WhatsAppBubble().then((m) => ({ default: m.WhatsAppBubble })));
const BookingSection = lazy(() => importers.BookingSection().then((m) => ({ default: m.BookingSection })));

// Wraps each deferred section in its OWN Suspense boundary. A single shared
// boundary would mean one still-loading section unmounts every OTHER
// already-rendered section back to its fallback while it waits -- which is
// what caused sections to vanish (and the page to lose most of its height)
// when several sections were force-mounted at once on narrower viewports.
const Deferred = ({ minHeight, id, children }) => (
  <Suspense fallback={null}>
    <DeferredMount minHeight={minHeight} id={id}>{children}</DeferredMount>
  </Suspense>
);

export const Hero = () => {
  useLenis();
  const mouse = useMousePosition();
  const { scrollY } = useScroll();

  useEffect(() => {
    // Fetch every below-the-fold chunk in the background once the page is
    // idle, without mounting/rendering it. This keeps the initial-paint
    // perf win (nothing heavy runs until needed) while ensuring a later
    // forced mount (e.g. clicking a nav link) never has to wait on a fresh
    // network fetch, which is what made scrollToSection unreliable.
    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    const id = schedule(() => {
      Object.values(importers).forEach((importFn) => importFn().catch(() => {}));
    });
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, []);

  return (
    <div className="font-body">
      <GlobalNoiseOverlay />
      <ScrollProgressBar />
      <CustomCursor />
      <Navbar />
      <ScrollYContext.Provider value={scrollY}>
        <main>
          <HeroCore />
          <SectionDivider />
          <Deferred minHeight={900}><DemoShowcase /></Deferred>
          <SectionDivider />
          <Deferred minHeight={700}><ResultsSection /></Deferred>
          <SectionDivider />
          <Deferred minHeight={800}><ConciergeSection /></Deferred>
          <SectionDivider />
          <Deferred minHeight={900}><CaseStudiesSection /></Deferred>
          <SectionDivider />
          <Deferred minHeight={900}><PricingSection /></Deferred>
          <SectionDivider />
          <Deferred minHeight={900} id="trusted-by"><TrustSection /></Deferred>
          <SectionDivider />
          <Deferred minHeight={600} id="showcase"><LaptopShowcase mouse={mouse} /></Deferred>
          <Deferred minHeight={300} id="results"><AnimatedStats /></Deferred>
          <Deferred minHeight={400} id="features"><FeatureCards /></Deferred>
          <Deferred minHeight={400} id="cta"><ClosingCtaBand /></Deferred>
          <SectionDivider />
          <Deferred minHeight={700}><BookingSection /></Deferred>
        </main>
      </ScrollYContext.Provider>
      <Suspense fallback={null}>
        <StickyTrustBar />
        <WhatsAppBubble />
      </Suspense>
    </div>
  );
};
